import * as React from "react";
import * as ReactDOM from "react-dom";
import * as enums from "../../enumerations";
import * as modelsGet from "../../models/get";
import * as modelsEnv from "../../models/environment";
import * as modelsCalendar from "../../models/calendar";
import * as calendarUtils from "../../utils/calendar";
import * as globals from "../../globals";
import * as apiAccounts from "../../api/accounts";
import { Header, HeaderMenuItem, HeaderBreadcrumbItem, HeaderItem, HeaderProfileItem, HeaderSeparator, HeaderCalendarItem } from "../header";
import { PopupProvider, PopupTrigger } from "../popup";
import { ISelectedObjectReturn } from "../../models/calendar";

interface ICalendarsInGroup
{
    id?: number,
    name?: string,
    color?: string
}

interface IProperties
{
    /**
     * Die momentane Route.
     */
    route?: uri.URI,

    /**
     * Die konvertierten Daten des Query Strings.
     */
    queryString?: modelsEnv.IQueryString,

    /**
     * Enthält den momentan ausgewählten Kalender oder die Kalendergruppe.
     */
    selectedObject: modelsCalendar.ISelectedObjectId,

    /**
     * Die selektierte Kalenderansicht.
     */
    selectedViewMode: enums.CalendarViewMode,

    /**
     * Die Anzahl aller Benachrichtigungen des Benutzers.
     */
    notificationsCount: number,

    /**
     * Ein Text der angezeigt wird, wenn route nicht der Kalender ist.
     */
    optionalHeaderText?: string,

    /**
     * Wird ausgeführt, sobald auf den Menu-Button geklickt wird.
     */
    onMenuClick: () => void,

    /**
     * Wird ausgeführt, sobald auf den "Suche" Button geklickt wird.
     */
    onSearchClick: () => void,

    /**
     * Wird ausgeführt, sobald auf den "Hilfe" Button geklickt wird.
     */
    onHelpClick: () => void,

    /**
     * Wird ausgeführt, sobald auf den "Benachrichtigungen" Button geklickt wird.
     */
    onNotificationsClick: () => void
}

interface IState
{
    /**
     * Die ID des selektierten Objekts.
     */
    objectId?: number,

    /**
     * Der Name des selektierten Objekts.
     */
    objectName?: string,

    /**
     * Der angemeldete Benutzer.
     */
    currentUser?: modelsGet.IUser,

    /**
     * Enthält alle Kalender die einer Kalendergruppe zugeordnet sind.
     */
    calendars?: ICalendarsInGroup[]
}

export class MainHeader extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see MainHeader.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);
        
        // State muss ein leeres Objekt sein. Es wird erst in der init Funktion gefüllt.
        this.state = {};

        // Alle benötigten Daten laden.
        this.initAsync(props);
    }

    /**
     * Wird ausgeführt, sobald die Komponente neue Eigenschaften erhält.
     * Hier werden die benötigten Daten erneut geladen.
     * @param nextProps Die nächsten/neuen Eigenschaften.
     */
    componentWillReceiveProps(nextProps: IProperties)
    {
        if (this.props.selectedObject != null)
        {
            const oldProp = this.props.selectedObject;
            const newProp = nextProps.selectedObject;

            if (oldProp.type !== newProp.type || oldProp.id !== newProp.id)
                this.initAsync(nextProps);
        }
    }

    /**
     * Lädt alle benötigten Daten, wie z. B. die Daten des momentan selektierten Kalenders oder der Kalendergruppe.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        // Benutzer laden.
        var currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();

        // Alle eingerichteten Kalendergruppen laden.
        var calendarGroups: modelsGet.ICalendarGroup[] = await globals.getCalendarGroupsAsync();

        // Alle eingerichteten Kalender laden.
        var calendars: modelsGet.ICalendar[] = await globals.getCalendarsAsync();

        // Die Daten des selektierten Objekts laden.
        var selectedObject: ISelectedObjectReturn;
        if (props.selectedObject == null)
        {
            this.setState({
                objectName: props.optionalHeaderText,
                currentUser: currentUser,
                calendars: null
            });
        }
        else
        {
            selectedObject = await calendarUtils.getSelectedObject(props.selectedObject, calendarGroups, calendars);

            // Wenn Daten von "Alle Kalender".
            selectedObject.all((cal) =>
            {
                this.setState({
                    objectId: -1,
                    objectName: "Alle Kalender",
                    currentUser: currentUser,
                    calendars: calendars
                });
            });

            // Wenn Daten von Kalender.
            selectedObject.calendar((cal) =>
            {
                this.setState({
                    objectId: cal.id,
                    objectName: cal.name,
                    currentUser: currentUser,
                    calendars: null
                });
            });

            // Wenn Daten von Kalendergruppe.
            selectedObject.calendarGroup((grp) =>
            {
                let calendarsInGroup: ICalendarsInGroup[] = [];
                for (var i = 0; i < grp.calendars.length; i++)
                {
                    calendarsInGroup.push({
                        id: grp.calendars[i].calendar_id,
                        name: grp.calendars[i].name,
                        color: grp.calendars[i].calendar_color
                    });
                }

                this.setState({
                    objectId: grp.id,
                    objectName: grp.group_name,
                    currentUser: currentUser,
                    calendars: calendarsInGroup
                });
            });
        }
    }

    /**
     * Rendert die linke Seite des Headers.
     */
    private renderLeftItems(): JSX.Element
    {
        return (
            <HeaderBreadcrumbItem text={this.state.objectName} />
        );
    }

    /**
     * Rendert die rechte Seite des headers.
     */
    private renderRightItems(): JSX.Element[]
    {
        const { notificationsCount } = this.props;
        const { currentUser } = this.state;

        if (currentUser != null)
        {
            const items: JSX.Element[] = [
                <HeaderItem
                    key={1}
                    icon="magnify"
                    onClick={this.props.onSearchClick} />,
                <HeaderItem
                    key={2}
                    icon="help-circle"
                    onClick={this.props.onHelpClick} />,
                <HeaderItem
                    key={3}
                    icon="bell"
                    notifierCount={notificationsCount}
                    onClick={this.props.onNotificationsClick} />,
                <HeaderSeparator key={4} />,
                <PopupProvider
                    key={5}
                    id="settings-popup"
                    trigger={PopupTrigger.Click}>
                    <HeaderItem
                        icon="settings" />
                </PopupProvider>,
                <PopupProvider
                    key={6}
                    id="profile-popup"
                    trigger={PopupTrigger.Click}>
                    <HeaderProfileItem
                        profilePicture={currentUser.avatar} />
                </PopupProvider>
            ];

            if (!currentUser.claims.can_see_settings)
                items.splice(4, 1);

            return items;
        }
        else
        {
            return null;
        }
    }

    /**
     * Rendert die Kalender wenn eine Kalendergruppe ausgewählt ist.
     */
    private renderCalendarItems(): JSX.Element[]
    {
        if (this.state.calendars != null && this.state.calendars.length > 0 && this.props.selectedViewMode !== enums.CalendarViewMode.planning)
        {
            const { calendars } = this.state;
            const { route, queryString } = this.props;
            
            return calendars.map((calendar) =>
            {
                route.setSearch("selected", calendar.id.toString());
                return (
                    <HeaderCalendarItem
                        key={calendar.id}
                        dataSourceId={calendar.id}
                        text={calendar.name}
                        color={calendar.color}
                        isSelected={queryString.selected === calendar.id}
                        linkTo={route.hrefRelative()} />
                );
            });
        }

        return null;
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        return (
            <Header
                leftItems={this.renderLeftItems()}
                rightItems={this.renderRightItems()}
                calendarItems={this.renderCalendarItems()}
                onMenuClick={this.props.onMenuClick} />
        );
    }
}